import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { analyticListLimit, analyticListLimits } from "configs/limits";
import Pagination from "components/Pagination";
import FilterTabs from "components/FilterTabs";
import EmptyState from "components/EmptyState/EmptyState";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";

interface ListItem {
  date: string;
  items: {
    typeTitle: string;
    usersCount: number;
    betCountReal: number;
    betSumReal: number;
    betCountBonus: number;
    betSumBonus: number;
    winCountReal: number;
    winSumReal: number;
    winCountBonus: number;
    winSumBonus: number;
    turnoverBonus: number;
    turnoverReal: number;
    turnoverTotal: number;
    ggrBonus: number;
    ggrReal: number;
    ggrTotal: number;
  }[];
}

const AnalyticSportsCompetitionsList: React.FC = () => {
  const { t } = useTranslation();
  const [isFetched, setIsFetched] = useState(false);
  const [dateFrom, setDateFrom] = useState(
    moment().subtract(1, "week").toDate(),
  );
  const [dateTo, setDateTo] = useState(new Date());
  const [type, setType] = useState("day");
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(analyticListLimit);
  const [items, setItems] = useState<ListItem[]>([]);

  const handleLoadItems = async () => {
    setIsFetched(false);
    try {
      const result = await instance.post(
        `${apiRoutes.sportCompetitionsAnalytics}`,
        {
          startDate: moment(dateFrom).format("DD.MM.YYYY"),
          endDate: moment(dateTo).format("DD.MM.YYYY"),
          period: type,
          offset: activePage - 1,
          limit: currentLimit,
        },
      );
      setItems(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetched(true);
    }
  };

  useEffect(() => {
    handleLoadItems();
  }, [dateFrom, dateTo, activePage, type, currentLimit]);

  return (
    <div className="page-list-wrapper analytic-wrapper">
      <div className="page-list-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            <div className="dashboard-filter">
              <div className="row">
                <div className="dashboard-date">
                  <span className="label">{t("from")}:</span>
                  <div className="date-picker-wrapper">
                    <button
                      onClick={() =>
                        setDateFrom(
                          moment(dateFrom).subtract(1, "days").toDate(),
                        )
                      }
                    >
                      &lt;
                    </button>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      selected={dateFrom}
                      onChange={(date: any) => setDateFrom(date)}
                      dateFormat={"d/MM/YYYY"}
                    />
                    <button
                      onClick={() =>
                        setDateFrom(moment(dateFrom).add(1, "days").toDate())
                      }
                    >
                      &gt;
                    </button>
                  </div>
                </div>
                <div className="dashboard-date">
                  <span className="label">{t("to")}:</span>
                  <div className="date-picker-wrapper">
                    <button
                      onClick={() =>
                        setDateTo(moment(dateTo).subtract(1, "days").toDate())
                      }
                    >
                      &lt;
                    </button>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      selected={dateTo}
                      onChange={(date: any) => setDateTo(date)}
                      dateFormat={"d/MM/YYYY"}
                    />
                    <button
                      onClick={() =>
                        setDateTo(moment(dateTo).add(1, "days").toDate())
                      }
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <FilterTabs
              value={type}
              onSelect={(e) => setType(e as string)}
              options={[
                { value: "hour", label: t("hour") },
                { value: "day", label: t("day") },
                { value: "week", label: t("week") },
                { value: "month", label: t("month") },
                { value: "year", label: t("year") },
              ]}
            />
          </div>
        </div>

        {!isFetched && (
          <>
            <DataTableSkeleton columnsCount={17} rowsCount={10} />
            <PaginationSkeleton />
          </>
        )}

        {isFetched && items.length === 0 && (
          <div className="game-table-wrap table">
            <EmptyState message={t("noCompetitionDataFound")} />
          </div>
        )}

        {isFetched && items.length > 0 && (
          <>
            <div className="game-table-wrap table">
              <table className="game-table">
                <thead>
                  <tr>
                    <th className="separator main">{t("date")}</th>
                    <th>{t("competition")}</th>
                    <th>{t("users")}</th>
                    <th>{t("betReal")} cnt</th>
                    <th>{t("betReal")} sum</th>
                    <th>{t("betBonus")} cnt</th>
                    <th>{t("betBonus")} sum</th>
                    <th>{t("winReal")} cnt</th>
                    <th>{t("winReal")} sum</th>
                    <th>{t("winBonus")} cnt</th>
                    <th>{t("winBonus")} sum</th>
                    <th>{t("realTo")}</th>
                    <th>{t("bonusTo")}</th>
                    <th>{t("totalTo")}</th>
                    <th>{t("realGgr")}</th>
                    <th>{t("bonusGgr")}</th>
                    <th>{t("totalGgr")}</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((el) =>
                    el.items.map((item, idx) => (
                      <tr key={`${el.date}-${idx}`}>
                        <td className="separator main">
                          {idx === 0
                            ? moment
                                .utc(el.date)
                                .format(
                                  type === "hour"
                                    ? "DD.MM.YYYY HH:mm"
                                    : "DD.MM.YYYY",
                                )
                            : ""}
                        </td>
                        <td>{item.typeTitle}</td>
                        <td>{item.usersCount}</td>
                        <td>{item.betCountReal}</td>
                        <td>{item.betSumReal}</td>
                        <td>{item.betCountBonus}</td>
                        <td>{item.betSumBonus}</td>
                        <td>{item.winCountReal}</td>
                        <td>{item.winSumReal}</td>
                        <td>{item.winCountBonus}</td>
                        <td>{item.winSumBonus}</td>
                        <td>{item.turnoverReal}</td>
                        <td>{item.turnoverBonus}</td>
                        <td>{item.turnoverTotal}</td>
                        <td>{item.ggrReal}</td>
                        <td>{item.ggrBonus}</td>
                        <td>{item.ggrTotal}</td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              activePage={activePage}
              limit={currentLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={analyticListLimits}
              handleLimitClick={(limit) => {
                setCurrentLimit(limit);
                setActivePage(1);
              }}
              handleNextClick={() => {
                if (activePage !== totalPages) setActivePage(activePage + 1);
              }}
              handlePrevClick={() => {
                if (activePage !== 1) setActivePage(activePage - 1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticSportsCompetitionsList;
